import { useCallback, useEffect, useMemo, useState } from 'react'
import Pagination from '../components/Pagination'
import { getPayouts, PayoutListParams } from '../../services/payoutsService'
import PayoutDetailDrawer from './components/PayoutDetailDrawer'

const ACTION_TYPES = [
  { value: 'experience', label: 'Experience' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'expertise', label: 'Expertise' },
]

const STATUS_OPTIONS = [
  { value: 'pending_payout', label: 'Pending Payout', cls: 'pending' },
  { value: 'in_transit', label: 'In Transit', cls: 'in-transit' },
  { value: 'paid', label: 'Paid', cls: 'paid' },
  { value: 'on_hold', label: 'On Hold', cls: 'on-hold' },
  { value: 'cancelled', label: 'Cancelled', cls: 'cancelled' },
  { value: 'refunded', label: 'Refunded', cls: 'refunded' },
]

const DEFAULT_STATUSES = ['pending_payout', 'in_transit']

const formatCurrency = (amount: any, code = 'USD') => {
  const n = Number(amount ?? 0)
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code || 'USD' }).format(n)
  } catch {
    return `${n.toFixed(2)} ${code}`
  }
}

const initialDateFrom = () => {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return d.toISOString()
}

const initials = (user: any) => {
  if (!user) return '·'
  const f = (user.firstName || '').trim()[0] || ''
  const l = (user.lastName || '').trim()[0] || ''
  const initial = `${f}${l}`.trim()
  return initial || (user.email || '·').slice(0, 1).toUpperCase()
}

const fullName = (user: any) => {
  if (!user) return '—'
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
  return name || user.email
}

const statusMeta = (value: string) => STATUS_OPTIONS.find((s) => s.value === value)

const labelOf = (source: { value: string; label: string }[], v: string) =>
  source.find((o) => o.value === v)?.label ?? v

export const PayoutsAdminPage: React.FC = () => {
  const limit = 50
  const [rows, setRows] = useState<any[]>([])
  const [summary, setSummary] = useState<any>(null)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const [filters, setFilters] = useState({
    actionType: [] as string[],
    status: DEFAULT_STATUSES,
    dateFrom: initialDateFrom(),
    dateTo: '',
  })

  const [selectedPayoutId, setSelectedPayoutId] = useState<number | null>(null)

  const buildParams = useCallback(
    (page: number): PayoutListParams => ({
      limit,
      offset: (page - 1) * limit,
      actionType: filters.actionType.join(',') || undefined,
      status: filters.status.join(',') || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
    }),
    [filters]
  )

  const fetchPage = useCallback(
    async (page: number) => {
      setLoading(true)
      try {
        const data = await getPayouts(buildParams(page))
        setRows(data.results ?? [])
        setCount(data.count ?? 0)
        setSummary(data.summary ?? null)
        setCurrentPage(page)
      } finally {
        setLoading(false)
      }
    },
    [buildParams]
  )

  useEffect(() => {
    fetchPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const totalPages = useMemo(() => Math.max(1, Math.ceil(count / limit)), [count])

  const addChip = (arr: string[], v: string) => (arr.includes(v) ? arr : [...arr, v])
  const removeChip = (arr: string[], v: string) => arr.filter((x) => x !== v)

  const clearFilters = () =>
    setFilters({
      actionType: [],
      status: DEFAULT_STATUSES,
      dateFrom: initialDateFrom(),
      dateTo: '',
    })

  const summaryLine = useMemo(() => {
    const parts: string[] = []
    parts.push(`Showing ${rows.length} of ${count} payouts`)
    if (filters.status.length) {
      const names = filters.status.map((s) => labelOf(STATUS_OPTIONS, s)).join(' + ')
      parts.push(names)
    }
    return parts.join(' · ')
  }, [rows.length, count, filters.status])

  return (
    <div className='nb-payouts'>
      {/* HEADER */}
      <div className='nb-payouts__header'>
        <div>
          <h1 className='nb-payouts__title'>Partner Payouts</h1>
          <div className='nb-payouts__meta'>{summaryLine}</div>
        </div>
        <div className='nb-payouts__actions'>
          <button
            className='nb-payouts__icon-btn'
            onClick={() => fetchPage(currentPage)}
            title='Refresh'
            type='button'
          >
            ↻
          </button>
        </div>
      </div>

      {/* KPI STRIP */}
      {summary && (
        <div className='nb-payouts__kpi-strip'>
          <div className='nb-payouts__kpi nb-payouts__kpi--pending'>
            <div className='nb-payouts__kpi-label'>Pending Payout</div>
            <div className='nb-payouts__kpi-value'>
              {formatCurrency(summary.pendingPayout?.amount ?? 0)}
            </div>
            <div className='nb-payouts__kpi-sub'>
              {summary.pendingPayout?.count ?? 0} payouts · USD equiv.
            </div>
          </div>
          <div className='nb-payouts__kpi nb-payouts__kpi--in-transit'>
            <div className='nb-payouts__kpi-label'>In Transit</div>
            <div className='nb-payouts__kpi-value'>
              {formatCurrency(summary.inTransit?.amount ?? 0)}
            </div>
            <div className='nb-payouts__kpi-sub'>
              {summary.inTransit?.count ?? 0} payouts · awaiting confirmation
            </div>
          </div>
          <div className='nb-payouts__kpi nb-payouts__kpi--paid'>
            <div className='nb-payouts__kpi-label'>Paid</div>
            <div className='nb-payouts__kpi-value'>
              {formatCurrency(summary.paid?.amount ?? 0)}
            </div>
            <div className='nb-payouts__kpi-sub'>
              {summary.paid?.count ?? 0} payouts · settled
            </div>
          </div>
          <div className='nb-payouts__kpi nb-payouts__kpi--commission'>
            <div className='nb-payouts__kpi-label'>Commission collected</div>
            <div className='nb-payouts__kpi-value'>
              {formatCurrency(summary.commissionTotal ?? 0)}
            </div>
            <div className='nb-payouts__kpi-sub'>Platform retained</div>
          </div>
        </div>
      )}

      {/* FILTER BAR */}
      <div className='nb-payouts__filter-bar'>
        {/* Action Type group */}
        <div className='nb-payouts__filter-group'>
          <span className='nb-payouts__filter-label'>Type</span>
          {filters.actionType.map((v) => (
            <span key={v} className='nb-payouts__chip'>
              {labelOf(ACTION_TYPES, v)}
              <span
                className='nb-payouts__chip__x'
                onClick={() =>
                  setFilters((f) => ({ ...f, actionType: removeChip(f.actionType, v) }))
                }
              >
                ×
              </span>
            </span>
          ))}
          <select
            className='nb-payouts__filter-select'
            value=''
            onChange={(e) => {
              if (!e.target.value) return
              setFilters((f) => ({ ...f, actionType: addChip(f.actionType, e.target.value) }))
              e.target.value = ''
            }}
          >
            <option value=''>+ Add</option>
            {ACTION_TYPES.filter((o) => !filters.actionType.includes(o.value)).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Status group */}
        <div className='nb-payouts__filter-group'>
          <span className='nb-payouts__filter-label'>Status</span>
          {filters.status.map((v) => (
            <span key={v} className='nb-payouts__chip'>
              {labelOf(STATUS_OPTIONS, v)}
              <span
                className='nb-payouts__chip__x'
                onClick={() =>
                  setFilters((f) => ({ ...f, status: removeChip(f.status, v) }))
                }
              >
                ×
              </span>
            </span>
          ))}
          <select
            className='nb-payouts__filter-select'
            value=''
            onChange={(e) => {
              if (!e.target.value) return
              setFilters((f) => ({ ...f, status: addChip(f.status, e.target.value) }))
              e.target.value = ''
            }}
          >
            <option value=''>+ Add</option>
            {STATUS_OPTIONS.filter((o) => !filters.status.includes(o.value)).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Range group */}
        <div className='nb-payouts__filter-group'>
          <span className='nb-payouts__filter-label'>Range</span>
          <input
            type='date'
            className='nb-payouts__filter-select'
            value={filters.dateFrom ? filters.dateFrom.slice(0, 10) : ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                dateFrom: e.target.value ? new Date(e.target.value).toISOString() : '',
              }))
            }
          />
          <span style={{ color: '#808080' }}>—</span>
          <input
            type='date'
            className='nb-payouts__filter-select'
            value={filters.dateTo ? filters.dateTo.slice(0, 10) : ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                dateTo: e.target.value ? new Date(e.target.value).toISOString() : '',
              }))
            }
          />
        </div>

        <button type='button' className='nb-payouts__clear-link' onClick={clearFilters}>
          Clear filters
        </button>
      </div>

      {/* TABLE */}
      <table className='nb-payouts__table'>
        <thead>
          <tr>
            <th>Charged at</th>
            <th>Action</th>
            <th>Member</th>
            <th>Partner</th>
            <th style={{ textAlign: 'right' }}>Gross</th>
            <th style={{ textAlign: 'right' }}>Commission</th>
            <th style={{ textAlign: 'right' }}>Payout</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={8} className='nb-payouts__table__empty'>Loading…</td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={8} className='nb-payouts__table__empty'>
                No payouts match the current filters.
              </td>
            </tr>
          )}
          {!loading &&
            rows.map((p) => {
              const s = statusMeta(p.status)
              const typeCls = `nb-payouts__type-badge nb-payouts__type-badge--${p.actionType}`
              return (
                <tr key={p.id} onClick={() => setSelectedPayoutId(p.id)}>
                  <td className='nb-payouts__row-charged-at'>
                    {new Date(p.chargedAt).toLocaleString(undefined, {
                      year: 'numeric', month: '2-digit', day: '2-digit',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  <td>
                    <div className={typeCls}>{p.actionType}</div>
                    <div className='nb-payouts__action-title'>{p.actionLabel}</div>
                  </td>
                  <td>
                    <span className='nb-payouts__party'>
                      <span className='nb-payouts__avatar'>{initials(p.member)}</span>
                      <span className='nb-payouts__party-name'>{fullName(p.member)}</span>
                    </span>
                  </td>
                  <td>
                    <span className='nb-payouts__party'>
                      <span className='nb-payouts__avatar'>{initials(p.partner)}</span>
                      <span className='nb-payouts__party-name'>{fullName(p.partner)}</span>
                    </span>
                  </td>
                  <td className='nb-payouts__amount'>
                    {formatCurrency(p.grossAmount, p.currency)}
                  </td>
                  <td className='nb-payouts__amount nb-payouts__amount--muted'>
                    {formatCurrency(p.commissionAmount, p.currency)}
                    <span className='nb-payouts__amount-suffix'>{p.commissionPercent}%</span>
                  </td>
                  <td className='nb-payouts__amount nb-payouts__amount--strong'>
                    {formatCurrency(p.payoutAmount, p.currency)}
                  </td>
                  <td>
                    {s && (
                      <span className={`nb-payouts__status nb-payouts__status--${s.cls}`}>
                        {s.label}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
        </tbody>
      </table>

      {/* PAGINATION */}
      <div className='nb-payouts__footer'>
        <div />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => fetchPage(p)}
        />
      </div>

      <PayoutDetailDrawer
        payoutId={selectedPayoutId}
        onClose={() => setSelectedPayoutId(null)}
        onChanged={() => fetchPage(currentPage)}
      />
    </div>
  )
}
